/**
 * SVG arrows + small annotations (OUT badge, runner #jersey, RUN tag) over the
 * field diamond when an at-bat completes. Pairs with `AtBatResultBanner` —
 * both consume the same lifecycle hook so they fade in lock-step.
 *
 * Collision-avoidance: annotations are placed perpendicular to each arrow at
 * its midpoint, on the OUTWARD (grass) side away from the mound. The arrow
 * itself still terminates exactly on the destination base.
 */
import type { LastAtBatResult, RunnerBase, RunnerMovement } from '../state/selectors';
import { useAtBatResultLifecycle } from '../hooks/useAtBatResultLifecycle';
import styles from './AtBatResultArrows.module.css';

interface AtBatResultArrowsProps {
  result: LastAtBatResult;
  dismissedAtBatIndex: number | null;
  replayKey: number;
}

interface Pos {
  x: number;
  y: number;
}

const BASE_POSITIONS: Record<'1B' | '2B' | '3B' | 'home', Pos> = {
  '1B': { x: 70, y: 50 },
  '2B': { x: 50, y: 30 },
  '3B': { x: 30, y: 50 },
  home: { x: 50, y: 70 },
};

const MOUND: Pos = { x: 50, y: 50 };

function basePoint(base: RunnerBase | null): Pos | null {
  if (!base) return null;
  if (base === 'score') return BASE_POSITIONS.home;
  if (base === '1B' || base === '2B' || base === '3B' || base === 'home') {
    return BASE_POSITIONS[base];
  }
  return null;
}

function endpointFor(runner: RunnerMovement): Pos | null {
  if (runner.isOut) {
    return basePoint(runner.outBase ?? runner.end ?? null);
  }
  return basePoint(runner.end);
}

function startFor(runner: RunnerMovement): Pos {
  return basePoint(runner.start) ?? BASE_POSITIONS.home;
}

/**
 * Pick a label position perpendicular to the arrow's midpoint, on the side
 * farther from the mound — i.e. on the grass between the baseline and the foul
 * line. Keeps OUT badges + jersey chips off the fielder/runner badges sitting
 * on the bases.
 */
function annotationOffset(start: Pos, end: Pos, distance: number): Pos {
  const mid = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const len = Math.hypot(dx, dy) || 1;
  const perpA: Pos = { x: mid.x + (-dy / len) * distance, y: mid.y + (dx / len) * distance };
  const perpB: Pos = { x: mid.x + (dy / len) * distance, y: mid.y + (-dx / len) * distance };
  const distA = Math.hypot(perpA.x - MOUND.x, perpA.y - MOUND.y);
  const distB = Math.hypot(perpB.x - MOUND.x, perpB.y - MOUND.y);
  return distA > distB ? perpA : perpB;
}

function arrowColor(runner: RunnerMovement): string {
  if (runner.isOut) return 'var(--c-danger, #dc2626)';
  if (runner.isScoringEvent) return 'var(--c-success, #16a34a)';
  return 'var(--c-warn, #f59e0b)';
}

function markerIdFor(runner: RunnerMovement): string {
  if (runner.isOut) return 'atbat-arrow-danger';
  if (runner.isScoringEvent) return 'atbat-arrow-success';
  return 'atbat-arrow-warn';
}

export default function AtBatResultArrows({
  result,
  dismissedAtBatIndex,
  replayKey,
}: AtBatResultArrowsProps): JSX.Element | null {
  const { visible } = useAtBatResultLifecycle({
    atBatIndex: result.atBatIndex,
    dismissedAtBatIndex,
    replayKey,
  });

  if (!visible) return null;

  return (
    <svg
      className={styles.arrowsSvg}
      viewBox="0 0 100 100"
      aria-hidden="true"
      focusable="false"
      data-testid="at-bat-result-arrows"
    >
      <defs>
        <marker
          id="atbat-arrow-warn"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="5"
          markerHeight="5"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--c-warn, #f59e0b)" />
        </marker>
        <marker
          id="atbat-arrow-success"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="5"
          markerHeight="5"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--c-success, #16a34a)" />
        </marker>
        <marker
          id="atbat-arrow-danger"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="5"
          markerHeight="5"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--c-danger, #dc2626)" />
        </marker>
      </defs>
      {result.runners.map((runner, idx) => {
        const start = startFor(runner);
        const end = endpointFor(runner);
        if (!end) return null;
        // Skip arrows that don't actually move (and aren't outs).
        if (start.x === end.x && start.y === end.y && !runner.isOut) return null;
        const color = arrowColor(runner);
        const annotationAt = annotationOffset(start, end, 5);
        const labelText = runner.isOut ? 'OUT' : runner.isScoringEvent ? 'RUN' : null;
        return (
          <g key={`${runner.id}-${idx}`} className={styles.arrowGroup}>
            <line
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke={color}
              strokeWidth={1.2}
              strokeLinecap="round"
              strokeDasharray="0.1 1.8"
              markerEnd={`url(#${markerIdFor(runner)})`}
            />
            {labelText && (
              <g>
                <circle
                  cx={annotationAt.x}
                  cy={annotationAt.y - 1.6}
                  r={2.6}
                  fill={runner.isOut ? 'var(--c-danger, #dc2626)' : 'var(--c-success, #16a34a)'}
                  stroke="var(--c-ink-0)"
                  strokeWidth={0.35}
                />
                <text
                  x={annotationAt.x}
                  y={annotationAt.y - 1.0}
                  textAnchor="middle"
                  fontSize={2.0}
                  fontWeight={800}
                  fill="var(--c-ink-0)"
                  pointerEvents="none"
                >
                  {labelText}
                </text>
              </g>
            )}
            {runner.jersey && (
              <text
                x={annotationAt.x}
                y={annotationAt.y + 2.6}
                textAnchor="middle"
                fontSize={2.2}
                fontWeight={700}
                fill="var(--c-ink-100, #0c111c)"
                stroke="var(--c-ink-0)"
                strokeWidth={0.55}
                paintOrder="stroke fill"
                pointerEvents="none"
              >
                #{runner.jersey}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
